import { useQueryClient } from "@tanstack/react-query";
import jwt from "jwt-decode";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AccountControllerService,
  AgentControllerService,
  AgentPolicyControllerService,
  AuthControllerService,
  TlxHttpResponse,
  TrustAiBoardControllerService,
  UserAuth,
} from "@/api";
import { AgentEstateService } from "@/api/temp/AgentEstateService";
import { AgentProtectionService } from "@/api/temp/AgentProtectionService";
import { GovernanceOverviewService } from "@/api/temp/GovernanceOverviewService";
import { TrustAIExecutiveService } from "@/api/temp/TrustAIExecutiveService";
import { clearFilterState } from "@/components/Filters/state";
import { saveUser, useUserDispatch } from "@/context/UserContext";
import { clearAgentSession } from "@/libs/agentSession";
import { AgentDetailsApi } from "@/pages/AgentEstate/agentApi";
import { AuthoredPolicyApi } from "@/pages/AgentEstate/authoredPolicyApi";
import { AgentPolicyApi } from "@/pages/AgentEstate/policyApi";
import { TrustAiBoardApi } from "@/pages/TrustAIExecutiveBoard/boardApi";
import { AuthorityModel, IUserInfo } from "@/types/api";
import { getLoginErrorMessage } from "./errors";
export interface UseLogin {
  isLoading: boolean;
  onLogin: (
    userRequest: UserAuth,
    SAMLToken?: string,
    SAMLTokenExpiryTime?: string,
    SAMLRefreshToken?: string,
  ) => void;
  errorMessage: string;
  isMFARequired: boolean;
  reset: () => void;
}

const agentSessionQueryMethods = new Set(
  [
    // Agent APIs move from the temp services to the generated ones as they ship.
    ...Object.getOwnPropertyNames(AgentControllerService),
    ...Object.getOwnPropertyNames(AgentPolicyControllerService),
    ...Object.getOwnPropertyNames(AgentEstateService),
    ...Object.getOwnPropertyNames(TrustAIExecutiveService),
    ...Object.getOwnPropertyNames(GovernanceOverviewService),
    ...Object.getOwnPropertyNames(AgentProtectionService),
    ...Object.getOwnPropertyNames(TrustAiBoardControllerService),
    // The UI-typed facades key their queries by their own method names, which
    // need not match a generated one.
    ...Object.keys(AgentDetailsApi),
    ...Object.keys(AgentPolicyApi),
    ...Object.keys(AuthoredPolicyApi),
    ...Object.keys(TrustAiBoardApi),
  ].filter(name => name.endsWith("UsingGet") || name.endsWith("UsingPost")),
);

const useLogin = (): UseLogin => {
  const { t } = useTranslation();
  const history = useNavigate();
  const userDispatch = useUserDispatch();
  const queryClient = useQueryClient();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isMFARequired, setMFARequired] = useState<boolean>(false);
  const pending = useRef(false);

  const reset = useCallback(() => {
    if (pending.current) return;
    setErrorMessage("");
    setMFARequired(false);
  }, []);

  const onLogin = useCallback(
    (
      userRequest: UserAuth,
      SAMLToken: string = "",
      SAMLTokenExpiryTime: string = "",
      SAMLRefreshToken: string = "",
    ) => {
      // A ref also blocks submissions arriving before React updates the button.
      if (pending.current) return;
      pending.current = true;
      setLoading(true);
      setErrorMessage("");
      let request: Promise<TlxHttpResponse>;

      if (SAMLToken.length > 10) {
        request = AuthControllerService.userInfoUsingGet({
          headers: {
            Authorization: `Bearer ${SAMLToken}`,
          },
        });
      } else if (userRequest.totp?.length === 6) {
        request = AuthControllerService.verifyTotpUsingPost({
          userRequest,
        });
      } else {
        request = AuthControllerService.userLoginUsingPost({
          userRequest,
          userType: "TENANT_USER",
        });
      }

      request
        .then(async response => {
          const data = response.data as UserData;
          if (data.mfaEnabled && !data.token) {
            setMFARequired(true);
            return;
          }
          const { user, headers } = await buildUserData(data, SAMLToken);
          return AccountControllerService.listAccountsUsingGet(
            { includePolicyCount: true, pageNo: 1, pageSize: 9999 },
            { headers },
          ).then(response => {
            clearFilterState(); // clear filter state on login success for all pages from local storage
            user.accounts = response.items;
            storeTokens(
              data.token || SAMLToken,
              data.tokenExpiryTime || SAMLTokenExpiryTime,
              data.refreshToken || SAMLRefreshToken,
            );
            saveUser(user);
            clearAgentSession();
            // Expiry can return to login without a document reload. Discard the
            // previous session's query results before the dashboard mounts.
            queryClient.removeQueries({
              predicate: query =>
                typeof query.queryKey[0] === "string" &&
                agentSessionQueryMethods.has(query.queryKey[0]),
            });
            userDispatch({ type: "LOGIN_SUCCESS", data: user });
            void history("/");
          });
        })
        .catch(error => {
          setErrorMessage(getLoginErrorMessage(error, t));
        })
        .finally(() => {
          pending.current = false;
          setLoading(false);
        });
    },
    [history, userDispatch, queryClient, t],
  );

  return {
    isLoading,
    onLogin,
    errorMessage,
    isMFARequired,
    reset,
  };
};

/**
 * Store token in localstorage
 * @param token string
 * @param tokenExpiryTime string
 * @param refreshToken string
 */
export const storeTokens = (
  token: string,
  tokenExpiryTime: string,
  refreshToken: string,
) => {
  localStorage.setItem("id_token", token);
  localStorage.setItem("tokenExpiryTime", tokenExpiryTime);
  localStorage.setItem("refreshToken", refreshToken);
};

export const getTokens = () => {
  const token = <string>localStorage.getItem("id_token");
  const tokenExpiryTime = <string>localStorage.getItem("tokenExpiryTime");
  const refreshToken = <string>localStorage.getItem("refreshToken");

  return {
    token,
    tokenExpiryTime,
    refreshToken,
  };
};

export interface UserData {
  userLoginId: string;
  "User Name": string;
  status: string;
  "Tenant ID": string;
  userType: string;
  "Tenant Name": string;
  skipSso: boolean;
  isTenantFederated: boolean;
  isUserExternalSSM: boolean;
  roles: IUserInfo["roles"];
  mfaEnabled: boolean;
  mfaEnableDate: string;
  isTemplatePolicySupported: boolean;
  isManagementTenant: boolean;
  configParameters: string;
  token: string;
  tokenExpiryTime: string;
  refreshToken: string;
}

export const buildUserData = async (data: UserData, SAMLToken: string = "") => {
  const user: IUserInfo = {
    userId: data.userLoginId,
    userName: data["User Name"],
    status: data.status,
    tenant: data["Tenant ID"],
    userType: data.userType,
    tenantName: data["Tenant Name"],
    skipSso: data.skipSso,
    isTenantFederated: data.isTenantFederated,
    isUserExternalSSM: data.isUserExternalSSM,
    roles: data.roles || [],
    mfaEnabled: data.mfaEnabled,
    mfaEnableDate: data.mfaEnableDate,
    isTemplatePolicySupported: data.isTemplatePolicySupported,
    // add whitelist here
    whiteLists: {
      isManagementTenant: data.isManagementTenant,
    },
    configParameters: JSON.parse(data.configParameters || "{}"),
  };

  const decodedToken: { Authority: object } = jwt(data.token || SAMLToken);
  user.authority = decodedToken.Authority as AuthorityModel;

  const headers = {
    Authorization: `Bearer ${data.token || SAMLToken}`,
    tenantId: data["Tenant ID"],
  };

  return { user, headers };
};

export default useLogin;
